import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader, Popover } from '@mantine/core';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { IoNotificationsOutline } from 'react-icons/io5';
import {
  sellerNotificationsApi,
  type SellerNotificationItem,
} from '@modules/seller-notifications/api/seller-notifications.api';
import {
  disableSellerWebPush,
  enableSellerWebPush,
  getCurrentPushSubscription,
  getWebPushPermission,
  isWebPushSupported,
} from '@modules/seller-notifications/web-push';
import { showSellerInboxToast } from '@modules/orders/new-order-toast';
import { wasSellerInboxWsRecent } from '@modules/orders/components/SellerNewOrderToastListener';
import { DoubleCheckModal } from '@shared/ui';
import './SellerNotificationsBell.css';

const UNREAD_QK = ['seller-notifications', 'unread-count'] as const;
const LIST_QK = ['seller-notifications', 'list'] as const;

function productIdFromData(data: unknown): number | null {
  if (data == null || typeof data !== 'object') return null;
  const raw = (data as { productId?: unknown }).productId;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function orderIdFromData(data: unknown): number | null {
  if (data == null || typeof data !== 'object') return null;
  const raw = (data as { orderId?: unknown }).orderId;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Уведомления без path в data — раздел по типу (как в приложении продавца). */
const PATH_BY_TYPE: Record<string, string> = {
  cpc_budget_exhausted: '/promotion',
  cpc_credit_exhausted: '/promotion',
  seller_profile_moderation: '/profile',
};

function pathFromData(data: unknown): string | null {
  if (data == null || typeof data !== 'object') return null;
  const { path, type } = data as { path?: unknown; type?: unknown };
  if (typeof path === 'string' && path.startsWith('/')) return path;
  return typeof type === 'string' ? (PATH_BY_TYPE[type] ?? null) : null;
}

function NotificationRow({
  item,
  onRead,
  onOpen,
}: {
  item: SellerNotificationItem;
  onRead: (id: number) => void;
  onOpen: (item: SellerNotificationItem) => void;
}) {
  const unread = item.status === 'UNREAD';
  return (
    <button
      type="button"
      className={`seller-notifications-bell__row ${unread ? 'seller-notifications-bell__row--unread' : ''}`}
      onClick={() => {
        if (unread) onRead(item.id);
        onOpen(item);
      }}
    >
      <div className="seller-notifications-bell__row-top">
        <span
          className={`seller-notifications-bell__title ${unread ? 'seller-notifications-bell__title--unread' : ''}`}
        >
          {item.title}
        </span>
        {unread ? <span className="seller-notifications-bell__dot" aria-hidden /> : null}
      </div>
      {item.body ? <p className="seller-notifications-bell__body">{item.body}</p> : null}
      <time className="seller-notifications-bell__time" dateTime={item.createdAt}>
        {dayjs(item.createdAt).locale('ru').format('DD MMM YYYY')}
      </time>
    </button>
  );
}

export function SellerNotificationsBell({ enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushPermission, setPushPermission] = useState<NotificationPermission | null>(
    null,
  );
  const [pushPermissionModal, setPushPermissionModal] = useState<
    null | 'ask' | 'blocked' | 'done'
  >(null);
  const pushSupported = isWebPushSupported();

  useEffect(() => {
    if (!pushSupported) return;
    void getWebPushPermission().then(setPushPermission);
    void getCurrentPushSubscription().then((sub) => setPushEnabled(Boolean(sub)));
  }, [pushSupported, opened]);

  const runEnablePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushError(null);
    try {
      await enableSellerWebPush();
      setPushEnabled(true);
      setPushPermission('granted');
      setPushPermissionModal('done');
    } catch (err) {
      setPushError(
        err instanceof Error ? err.message : 'Не удалось включить пуш-уведомления',
      );
      const permission = await getWebPushPermission();
      if (permission === 'denied') {
        setPushPermissionModal('blocked');
      }
    } finally {
      setPushBusy(false);
    }
  };

  const togglePush = async () => {
    if (pushBusy) return;
    setPushError(null);

    if (pushEnabled) {
      setPushBusy(true);
      try {
        await disableSellerWebPush();
        setPushEnabled(false);
      } catch (err) {
        setPushError(
          err instanceof Error ? err.message : 'Не удалось выключить пуш-уведомления',
        );
      } finally {
        setPushBusy(false);
      }
      return;
    }

    // Сначала наша плашка — по кнопке вылезет системный запрос Chrome
    setOpened(false);
    const permission = await getWebPushPermission();
    if (permission === 'denied') {
      setPushPermissionModal('blocked');
      return;
    }
    if (permission === 'granted') {
      await runEnablePush();
      return;
    }
    setPushPermissionModal('ask');
  };

  const { data: unreadData } = useQuery({
    queryKey: UNREAD_QK,
    queryFn: () => sellerNotificationsApi.getUnreadCount(),
    enabled,
    refetchInterval: enabled ? 20_000 : false,
  });
  const unreadCount = unreadData?.count ?? 0;
  const prevUnreadRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      prevUnreadRef.current = null;
      return;
    }
    // Пока нет ответа API — не ставим baseline (иначе 0→N после reload даёт ложный тост)
    if (unreadData == null) return;

    if (prevUnreadRef.current == null) {
      prevUnreadRef.current = unreadData.count;
      return;
    }
    if (unreadData.count > prevUnreadRef.current && !wasSellerInboxWsRecent()) {
      showSellerInboxToast({
        title: 'Новое уведомление',
        body: 'Откройте колокольчик, чтобы посмотреть',
      });
      void queryClient.invalidateQueries({ queryKey: LIST_QK });
    }
    prevUnreadRef.current = unreadData.count;
  }, [enabled, unreadData, queryClient]);

  const {
    data: listData,
    isLoading: listLoading,
    isFetching,
  } = useQuery({
    queryKey: LIST_QK,
    queryFn: () => sellerNotificationsApi.list({ limit: 40 }),
    enabled: enabled && opened,
  });

  const markReadMut = useMutation({
    mutationFn: (id: number) => sellerNotificationsApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_QK });
      void queryClient.invalidateQueries({ queryKey: LIST_QK });
    },
  });

  const markAllMut = useMutation({
    mutationFn: () => sellerNotificationsApi.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_QK });
      void queryClient.invalidateQueries({ queryKey: LIST_QK });
    },
  });

  const items = listData?.items ?? [];
  const showLoader = listLoading || (isFetching && items.length === 0);

  return (
    <div className="seller-notifications-bell__target-wrap">
      <Popover
        position="bottom-end"
        withArrow={false}
        shadow="none"
        opened={opened}
        onChange={setOpened}
        closeOnClickOutside
        withinPortal
        trapFocus={false}
        zIndex={1200}
        offset={8}
      >
        <Popover.Target>
          <button
            type="button"
            aria-label="Уведомления"
            aria-expanded={opened}
            className={[
              'seller-notifications-bell__trigger',
              opened ? 'seller-notifications-bell__trigger--open' : '',
              !enabled ? 'seller-notifications-bell__trigger--muted' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setOpened((o) => !o)}
          >
            <IoNotificationsOutline />
          </button>
        </Popover.Target>
        <Popover.Dropdown className="seller-notifications-bell__dropdown">
          <div className="seller-notifications-bell__dropdown-header">
            <h2 className="seller-notifications-bell__dropdown-title">Уведомления</h2>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="seller-notifications-bell__mark-all"
                disabled={markAllMut.isPending}
                onClick={() => markAllMut.mutate()}
              >
                {markAllMut.isPending ? '…' : 'Прочитать все'}
              </button>
            ) : null}
          </div>

          {pushSupported ? (
            <div className="seller-notifications-bell__push">
              <div className="seller-notifications-bell__push-text">
                <strong>Пуши на этом устройстве</strong>
                <span>Заказы, отмены, чаты и все уведомления</span>
                {pushPermission ? (
                  <span className="seller-notifications-bell__push-status">
                    Статус браузера:{' '}
                    {pushPermission === 'granted'
                      ? 'разрешено'
                      : pushPermission === 'denied'
                        ? 'заблокировано'
                        : 'ещё не спрашивали'}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className={[
                  'seller-notifications-bell__push-toggle',
                  pushEnabled ? 'seller-notifications-bell__push-toggle--on' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={pushBusy}
                onClick={() => void togglePush()}
              >
                {pushBusy ? '…' : pushEnabled ? 'Вкл' : 'Выкл'}
              </button>
              {pushError ? (
                <p className="seller-notifications-bell__push-error">{pushError}</p>
              ) : null}
            </div>
          ) : null}

          {showLoader ? (
            <div className="seller-notifications-bell__state">
              <Loader size="sm" color="var(--accent)" />
            </div>
          ) : items.length === 0 ? (
            <div className="seller-notifications-bell__empty">
              <p className="seller-notifications-bell__empty-title">Нет уведомлений</p>
              <p className="seller-notifications-bell__empty-text">
                Здесь появятся новости по заказам и товарам.
              </p>
            </div>
          ) : (
            <div className="seller-notifications-bell__scroll">
              <div className="seller-notifications-bell__list">
                {items.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    onRead={(id) => markReadMut.mutate(id)}
                    onOpen={(n) => {
                      const path = pathFromData(n.data);
                      const orderId = orderIdFromData(n.data);
                      const productId = productIdFromData(n.data);
                      setOpened(false);
                      if (path) {
                        navigate(path);
                      } else if (orderId != null) {
                        navigate(`/orders/${orderId}`);
                      } else if (productId != null) {
                        navigate(`/products/${productId}`);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </Popover.Dropdown>
      </Popover>

      {enabled && unreadCount > 0 ? (
        <span className="seller-notifications-bell__badge" aria-hidden>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      ) : null}

      <DoubleCheckModal
        isOpen={pushPermissionModal === 'ask'}
        onClose={() => setPushPermissionModal(null)}
        title="Пуш-уведомления"
        message="Разрешите уведомления, чтобы сразу узнавать о новых заказах, отменах, сообщениях в чатах и других уведомлениях — даже когда кабинет свёрнут."
        firstButtonText="Не сейчас"
        secondButtonText="Разрешить"
        secondButtonAction={() => {
          void runEnablePush();
        }}
        isLoading={pushBusy}
      />

      <DoubleCheckModal
        isOpen={pushPermissionModal === 'blocked'}
        onClose={() => setPushPermissionModal(null)}
        title="Уведомления заблокированы"
        message={
          <>
            Браузер уже запретил уведомления для этого сайта, поэтому системный
            запрос больше не появляется.
            <br />
            <br />
            Chrome → ⋮ → Настройки сайта → Уведомления → Разрешить, затем снова
            нажмите «Вкл».
          </>
        }
        firstButtonText="Понятно"
        secondButtonText="Закрыть"
        secondButtonAction={() => setPushPermissionModal(null)}
      />

      <DoubleCheckModal
        isOpen={pushPermissionModal === 'done'}
        onClose={() => setPushPermissionModal(null)}
        title="Пуши включены"
        message={
          <>
            Сейчас должно прийти тестовое уведомление
            <br />
            <br />
            Смахните его влево и перейдите в настройки уведомлений (или перейдите вручную), включите все тумблеры (звук,
            экран блокировки).
          </>
        }
        firstButtonText="Понятно"
        secondButtonText="Закрыть"
        secondButtonAction={() => setPushPermissionModal(null)}
      />
    </div>
  );
}
